import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Sparkles, X, Send, Volume2, Loader2, ArrowLeft, Check } from 'lucide-react';
import {
  BUDDY_ERRORS, BuddyMsg, LANGUAGES, LanguageId, MAX_VOICE_CHARS, POPULAR_LANGUAGE_IDS, VOICES, VoiceId,
  askBuddy, textToVoice, uploadQuestionVoice,
} from '../lib/buddy';

interface Props {
  light: boolean;
  partnerName: string;
  platform: string;
  existingQuestions: string[];
  signedIn: boolean;
  canVoice: boolean;                 // offer the text-to-voice option
  questionId: string;                // the question card this was opened from
  initialText: string;               // what's already typed in that card
  initialMode: 'chat' | 'voice';
  onUseText: (text: string) => void;
  onAttachVoice: (audioUrl: string, text: string) => void;
  onClose: () => void;
}

const RED = '#dc2626';

export default function QuestionBuddy(p: Props) {
  const c = p.light
    ? { card: '#ffffff', surface: '#f4f4f5', text: '#18181b', muted: '#71717a', border: '#e4e4e7' }
    : { card: '#111114', surface: '#1c1c21', text: '#f4f4f5', muted: '#a1a1aa', border: '#2a2a31' };

  const name = p.partnerName.trim() || 'them';
  const greeting: BuddyMsg = {
    role: 'assistant',
    content: `Hey! 👋 I'm Shennel. What do you wanna ask ${name}? Tell me what's on your mind, or tap "Help me decide" and I'll throw some ideas at you.`,
  };

  const [mode, setMode] = useState<'chat' | 'voice'>(p.initialMode);
  const [msgs, setMsgs] = useState<BuddyMsg[]>([greeting]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [voiceText, setVoiceText] = useState(p.initialText);
  const [voice, setVoice] = useState<VoiceId>('Kore');
  const [language, setLanguage] = useState<LanguageId>('none');
  const [showLangs, setShowLangs] = useState(false);
  const [langQuery, setLangQuery] = useState('');
  const [clip, setClip] = useState<{ blob: Blob; url: string; seconds: number; forText: string; forVoice: VoiceId; forLanguage: LanguageId; spokenText?: string } | null>(null);
  const [genBusy, setGenBusy] = useState(false);
  const [attachBusy, setAttachBusy] = useState(false);
  const [voiceError, setVoiceError] = useState('');

  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs, busy, mode]);
  useEffect(() => () => { if (clip) URL.revokeObjectURL(clip.url); }, [clip]);

  const started = msgs.some(m => m.role === 'user');

  const send = async (text: string) => {
    const t = text.trim();
    if (!t || busy) return;
    setError(''); setInput('');
    const next = [...msgs, { role: 'user' as const, content: t }];
    setMsgs(next);
    setBusy(true);
    const res = await askBuddy(next.slice(1), {           // slice(1): the local greeting isn't sent
      partnerName: p.partnerName, platform: p.platform, existingQuestions: p.existingQuestions,
    });
    setBusy(false);
    if ('error' in res) { setError(BUDDY_ERRORS[res.error] ?? BUDDY_ERRORS.server_error); return; }
    setMsgs([...next, { role: 'assistant', content: res.reply, suggestions: res.suggestions }]);
  };

  const openVoice = (text: string) => {
    setVoiceText(text.slice(0, MAX_VOICE_CHARS));
    setVoiceError(''); setMode('voice');
  };

  const generate = async () => {
    const t = voiceText.trim();
    if (t.length < 2) return setVoiceError('Type what the voice should say.');
    if (!p.signedIn) return setVoiceError(BUDDY_ERRORS.sign_in_required);
    setVoiceError(''); setGenBusy(true);
    const res = await textToVoice(t, voice, language);
    setGenBusy(false);
    if ('error' in res) return setVoiceError(BUDDY_ERRORS[res.error] ?? BUDDY_ERRORS.server_error);
    setClip({ blob: res.blob, url: URL.createObjectURL(res.blob), seconds: res.seconds, forText: t, forVoice: voice, forLanguage: language, spokenText: res.spokenText });
  };

  const attach = async () => {
    if (!clip) return;
    setAttachBusy(true); setVoiceError('');
    try {
      const url = await uploadQuestionVoice(p.questionId, clip.blob);
      p.onAttachVoice(url, clip.forText);
    } catch {
      setVoiceError("Couldn't save the voice note. Please try again.");
      setAttachBusy(false);
    }
  };

  const langLabel = (id: LanguageId) => LANGUAGES.find(l => l.id === id)?.label ?? '';
  const totalLangs = LANGUAGES.length - 1;                 // minus "As I typed"
  const q = langQuery.trim().toLowerCase();
  const filteredLangs = LANGUAGES.filter(l => l.id !== 'none' && (!q || `${l.label} ${l.alt ?? ''}`.toLowerCase().includes(q)));
  const chipIds: LanguageId[] = ['none', ...POPULAR_LANGUAGE_IDS, ...(language !== 'none' && !POPULAR_LANGUAGE_IDS.includes(language) ? [language] : [])];
  const pickLanguage = (id: LanguageId) => { setLanguage(id); setShowLangs(false); setLangQuery(''); };
  const clipIsStale = !!clip && (clip.forText !== voiceText.trim() || clip.forVoice !== voice || clip.forLanguage !== language);
  const chip: React.CSSProperties = { fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 999, border: `1.5px solid ${c.border}`, background: 'transparent', color: c.text, cursor: 'pointer', fontFamily: 'inherit' };
  const btn = (primary: boolean, disabled = false): React.CSSProperties => ({
    fontSize: 12, fontWeight: 800, padding: '8px 14px', borderRadius: 10, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
    border: primary ? 'none' : `1.5px solid ${c.border}`, background: primary ? RED : 'transparent', color: primary ? '#fff' : c.text, opacity: disabled ? 0.5 : 1,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={p.onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        style={{ position: 'relative', width: '100%', maxWidth: 480, height: 'min(88vh, 660px)', background: c.card, borderRadius: '24px 24px 0 0', border: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${c.border}` }}>
          {mode === 'voice' ? (
            <button onClick={() => setMode('chat')} aria-label="Back to chat" style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.muted, display: 'flex' }}><ArrowLeft size={18} /></button>
          ) : (
            <span style={{ width: 32, height: 32, borderRadius: 10, background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Sparkles size={16} color="#fff" /></span>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: c.text }}>{mode === 'voice' ? 'Make it a voice note' : 'Shennel'}</div>
            <div style={{ fontSize: 11, color: c.muted, fontWeight: 600 }}>{mode === 'voice' ? 'A bot voice speaks it, so your real voice is never heard' : 'Your friend for deciding what to ask'}</div>
          </div>
          <button onClick={p.onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.muted, display: 'flex' }}><X size={18} /></button>
        </div>

        {mode === 'chat' ? (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {msgs.map((m, i) => (
                <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                  <div style={{ padding: '10px 14px', borderRadius: 18, fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap',
                    background: m.role === 'user' ? RED : c.surface, color: m.role === 'user' ? '#fff' : c.text,
                    borderBottomRightRadius: m.role === 'user' ? 6 : 18, borderBottomLeftRadius: m.role === 'user' ? 18 : 6 }}>
                    {m.content}
                  </div>
                  {m.suggestions?.map((s, j) => (
                    <div key={j} style={{ marginTop: 8, padding: 12, borderRadius: 14, border: `1.5px solid ${c.border}`, background: c.card }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 10 }}>“{s}”</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button onClick={() => p.onUseText(s)} style={btn(true)}><Check size={13} /> Use this</button>
                        {p.canVoice && <button onClick={() => openVoice(s)} style={btn(false)}><Volume2 size={13} /> Voice note</button>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {busy && <div style={{ alignSelf: 'flex-start', color: c.muted, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><Loader2 size={14} className="animate-spin" /> Shennel is typing…</div>}
              {error && <div style={{ color: RED, fontSize: 13, fontWeight: 600 }}>{error}</div>}
              {!started && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={chip} onClick={() => send("I'm not sure what to ask. Can you help me decide?")}>Help me decide</button>
                  <button style={chip} onClick={() => send('I want something light and fun to ask.')}>Something light & fun</button>
                  <button style={chip} onClick={() => send('I want to ask something serious.')}>Something serious</button>
                  {p.initialText.trim() && <button style={chip} onClick={() => send(`Can you make this question better: "${p.initialText.trim()}"`)}>Make my question better</button>}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${c.border}`, alignItems: 'flex-end' }}>
              <textarea
                value={input} rows={1} maxLength={600}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder="Tell Shennel what's on your mind…"
                style={{ flex: 1, resize: 'none', maxHeight: 96, padding: '10px 14px', borderRadius: 14, border: `1.5px solid ${c.border}`, background: c.surface, color: c.text, fontSize: 15, fontFamily: 'inherit', outline: 'none' }}
              />
              <button onClick={() => send(input)} disabled={busy || !input.trim()} aria-label="Send"
                style={{ width: 42, height: 42, borderRadius: 14, border: 'none', background: RED, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: busy || !input.trim() ? 0.5 : 1 }}>
                <Send size={17} />
              </button>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 800, color: c.muted, textTransform: 'uppercase', letterSpacing: 1 }}>What should it say?</label>
            <textarea
              value={voiceText} rows={4} maxLength={MAX_VOICE_CHARS}
              onChange={e => setVoiceText(e.target.value)}
              placeholder="Type your question…"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '12px 14px', borderRadius: 14, border: `1.5px solid ${c.border}`, background: c.surface, color: c.text, fontSize: 15, fontFamily: 'inherit', outline: 'none', resize: 'none' }}
            />
            <div style={{ textAlign: 'right', fontSize: 11, color: c.muted, marginTop: 4 }}>{voiceText.length}/{MAX_VOICE_CHARS}</div>

            <div style={{ fontSize: 12, fontWeight: 800, color: c.muted, textTransform: 'uppercase', letterSpacing: 1, margin: '14px 0 8px' }}>Speak it in</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {chipIds.map(id => (
                <button key={id} onClick={() => pickLanguage(id)}
                  style={{ ...chip, borderColor: language === id ? RED : c.border, color: language === id ? RED : c.text }}>
                  {langLabel(id)}
                </button>
              ))}
              <button onClick={() => setShowLangs(v => !v)}
                style={{ ...chip, borderStyle: 'dashed', borderColor: showLangs ? RED : c.border, color: showLangs ? RED : c.muted }}>
                {showLangs ? 'Close list' : `More languages (${totalLangs})`}
              </button>
            </div>
            {showLangs && (
              <div style={{ marginTop: 10, border: `1.5px solid ${c.border}`, borderRadius: 14, overflow: 'hidden' }}>
                <input
                  autoFocus value={langQuery} onChange={e => setLangQuery(e.target.value)}
                  placeholder="Search languages…"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', border: 'none', borderBottom: `1px solid ${c.border}`, background: c.surface, color: c.text, fontSize: 16, fontFamily: 'inherit', outline: 'none' }}
                />
                <div style={{ maxHeight: 240, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  {filteredLangs.length === 0 && <div style={{ padding: 14, fontSize: 13, color: c.muted }}>No language matches “{langQuery}”.</div>}
                  {filteredLangs.map(l => (
                    <button key={l.id} onClick={() => pickLanguage(l.id)}
                      style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', border: 'none', borderBottom: `1px solid ${c.border}`, background: language === l.id ? c.surface : 'transparent', color: language === l.id ? RED : c.text, fontSize: 14, fontWeight: language === l.id ? 800 : 600, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
                      <span>{l.label}</span>
                      {language === l.id && <Check size={15} />}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {language !== 'none' && (
              <div style={{ fontSize: 12, color: c.muted, marginTop: 8, lineHeight: 1.4 }}>
                Type in English and we'll translate it, then the voice speaks it. Translation is done by AI and can slip up, so listen to it before you add it.
              </div>
            )}

            <div style={{ fontSize: 12, fontWeight: 800, color: c.muted, textTransform: 'uppercase', letterSpacing: 1, margin: '14px 0 8px' }}>Pick a voice</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {VOICES.map(v => (
                <button key={v.id} onClick={() => setVoice(v.id)}
                  style={{ ...chip, borderColor: voice === v.id ? RED : c.border, color: voice === v.id ? RED : c.text }}>
                  {v.label}
                </button>
              ))}
            </div>

            <button onClick={generate} disabled={genBusy} style={{ ...btn(true, genBusy), marginTop: 18, width: '100%', justifyContent: 'center', padding: '12px 14px', fontSize: 14 }}>
              {genBusy ? <><Loader2 size={15} className="animate-spin" /> Making your voice note…</> : <><Volume2 size={15} /> {clip ? 'Make it again' : 'Make voice note'}</>}
            </button>

            {clip && (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 14, border: `1.5px solid ${c.border}` }}>
                <audio src={clip.url} controls style={{ width: '100%' }} />
                {clip.spokenText && (
                  <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: c.surface }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: c.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                      It says ({langLabel(clip.forLanguage)})
                    </div>
                    <div style={{ fontSize: 14, color: c.text, lineHeight: 1.45 }}>{clip.spokenText}</div>
                  </div>
                )}
                {clipIsStale && <div style={{ fontSize: 12, color: c.muted, marginTop: 8 }}>You changed the text, voice or language. Tap “Make it again” to update it.</div>}
                <button onClick={attach} disabled={attachBusy || clipIsStale} style={{ ...btn(true, attachBusy || clipIsStale), marginTop: 12, width: '100%', justifyContent: 'center', padding: '11px 14px', fontSize: 14 }}>
                  {attachBusy ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><Check size={15} /> Add to my question</>}
                </button>
              </div>
            )}
            {voiceError && <div style={{ color: RED, fontSize: 13, fontWeight: 600, marginTop: 12 }}>{voiceError}</div>}
          </div>
        )}
      </motion.div>
    </div>
  );
}
